import { reactive, readonly } from 'vue';

/**
 * Mandatory / optional app-update gate for the Flutter WebView.
 *
 * Two entry points:
 *  - check(axios): called once, very early at startup. Hits GET /api/app/version.
 *    The backend decides mobile-vs-browser from the existing X-Device-* headers,
 *    so a plain browser always gets update_required=false and never sees the
 *    overlay. Compared by versionCode server-side.
 *  - triggerFrom426(data): called by the axios interceptor when ANY protected
 *    API returns 426 APP_UPDATE_REQUIRED. This is the SERVER-enforced path, so a
 *    student cannot bypass a forced update by manipulating the frontend — the
 *    next protected request re-raises the block.
 *
 * A failed version-check request must NEVER take the app offline (it is an
 * ordinary HTTP response, so OfflineManager ignores it) and must never hard-fail
 * startup — enforcement still lives on the server (the 426 gate).
 */
const state = reactive({
  updateRequired: false, // drives the overlay
  forceUpdate: false,    // true => no bypass, no "لاحقًا"
  dismissed: false,      // optional update the student chose to postpone
  currentVersion: null,
  requiredVersion: null,
  updateUrl: null,
  message: '',
});

let checked = false;

function apply(payload) {
  if (!payload) return;
  if (payload.current_version != null) state.currentVersion = payload.current_version;
  if (payload.required_version != null) state.requiredVersion = payload.required_version;
  if (payload.update_url != null) state.updateUrl = payload.update_url;
  if (payload.message) state.message = payload.message;
  state.forceUpdate = !!payload.force_update;

  const required = !!payload.update_required;
  // An optional update the student already postponed stays hidden until a hard
  // (forced) block arrives.
  state.updateRequired = required && (state.forceUpdate || !state.dismissed);
}

async function check(axios) {
  if (checked) return;
  checked = true;
  try {
    const { data } = await axios.get('/app/version');
    apply(data);
  } catch (e) {
    // Do not block the app or trip the offline screen on a failed check.
    // eslint-disable-next-line no-console
    console.warn('[AppUpdate] startup version check failed — continuing', e);
  }
}

/** Hard server-side block (HTTP 426). Always forces the overlay, unbypassable. */
function triggerFrom426(data) {
  state.dismissed = false;
  apply({
    update_required: true,
    force_update: true,
    current_version: data?.current_version,
    required_version: data?.required_version,
    update_url: data?.update_url,
    message: data?.message,
  });
}

function openUpdate() {
  if (state.updateUrl) {
    window.open(state.updateUrl, '_blank');
  }
}

/** Postpone an OPTIONAL update (force_update = false). Never works when forced. */
function dismissOptional() {
  if (!state.forceUpdate) {
    state.dismissed = true;
    state.updateRequired = false;
  }
}

export const appUpdateState = readonly(state);

export const AppUpdateManager = {
  check,
  triggerFrom426,
  openUpdate,
  dismissOptional,
  state: appUpdateState,
};

export default AppUpdateManager;
